<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("products") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="listing.numberOfItems"
					:currentPage="page"
				/>
			</template>
			<template slot="actions">
				<j-button v-can="'writeProducts'" :to="{ name: 'ProductCreate' }">
					<crm-icon
						name="add"
						size="small"
						color="c5"
						v-can="'writeProducts'"
					/>
					{{ $t("create_product") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Search -->
		<div class="flex wrap xs-12 xs-left mb2">
			<div class="flex xs-12">
				<j-base-input-search
					:placeholder="$t('search_name')"
					:value="searchTerm"
					@input="onSearch($event)"
				/>
				<j-button
					class="ml2"
					type="tertiary"
					v-if="searchTerm"
					@click="cancelSearch"
					>{{ $t("cancel") }}</j-button
				>
			</div>
		</div>

		<!-- Data Container -->
		<j-card class="bg5">
			<j-data-table
				:header="header"
				:list="computedList"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template>
							<!-- Name -->
							<template v-if="column.field === 'name'">
								<router-link :to="item.linkToDetail">
									{{ item.name }}
								</router-link>
							</template>

							<!-- Delete -->
							<template v-else-if="column.field === 'actions'">
								<crm-icon
									name="delete"
									color="cError"
									@click.native="
										showDeleteDialog(item.plan, item.version, item.typeEnum)
									"
									v-can="'writeProducts'"
								/>
							</template>

							<!-- Others -->
							<template v-else>{{ item[column.field] }}</template>
						</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="listing.numberOfItems"
				:perPage="itemsPerPage"
				:activePage="page"
				@click="onPageChange($event)"
			/>
		</div>

		<!-- Delete Confirmation Modal -->
		<j-dialog
			:title="$t('delete_product')"
			type="delete"
			@confirm="onDelete(selectedId)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_product") }}</j-dialog
		>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import JDialog from "@/components/JDialog/"
import dayjs from "dayjs"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JDialog
	},

	data() {
		return {
			isLoading: false,

			// Actual fetched data
			listing: {
				numberOfItems: 0,
				list: []
			},

			// Search term
			searchTerm: null,

			// Pagination
			page: 1,
			itemsPerPage: 30,

			// Header of table
			header: [
				{
					field: "name",
					text: this.$t("name")
				},
				{
					field: "plan",
					text: this.$t("plan")
				},
				{
					field: "version",
					text: this.$t("version")
				},
				{
					field: "type",
					text: this.$t("type")
				},
				{
					field: "insurerName",
					text: this.$t("insurer")
				},
				{
					text: this.$t("created"),
					field: "createdAt"
				},
				{
					text: this.$t("created_by"),
					field: "createdBy"
				},
				{
					field: "actions",
					label: this.$t("actions")
				}
			],

			selectedProduct: {
				plan: null,
				type: null,
				version: null
			}
		}
	},

	computed: {
		/**
		 * Compute fetch data for data table
		 * This is what we are gonna display
		 */
		computedList() {
			return this.listing.list.map(item => ({
				name: item.name,
				plan: item?.productId?.plan,
				version: item?.productId?.version,
				typeEnum: item?.productId?.type, // original type name
				type: item.productId
					? this.$t(`insurance_types.${item.productId.type}`)
					: null, // translated type name to display
				insurerName: item?.insurer?.name,
				createdBy: item?.createdBy?.associatedUser?.name,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null,
				linkToDetail: {
					name: "ProductDetail",
					params: {
						plan: item?.productId?.plan,
						type: item?.productId?.type,
						version: item?.productId?.version
					}
				}
			}))
		}
	},

	methods: {
		onPageChange(pageIndex) {
			this.page = pageIndex
			this.readAll()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value = null) {
			this.searchTerm = value === null || value === "" ? null : value.trim()
			this.page = 1
			this.readAll()
		}, 200),

		cancelSearch() {
			this.searchTerm = null
			this.page = 1
			this.onSearch()
		},

		async readAll() {
			// Sorting - sort by createdAt
			let sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.page - 1) * this.itemsPerPage
			const limit = this.itemsPerPage

			// Search
			let where = this.searchTerm
				? {
						and: [
							{
								name_contains: this.searchTerm
							},
							{
								tags_some: { type: "product" }
							}
						]
				  }
				: {
						tags_some: { type: "product" }
				  }

			this.isLoading = true

			const query = gql`
				query products(
					$skip: Int
					$limit: Int
					$sort: sortInput
					$where: productWhereInput
				) {
					products_2(skip: $skip, limit: $limit, sort: $sort, where: $where) {
						totalCount
						list {
							createdAt
							createdBy {
								associatedUser {
									id
									name
								}
							}
							lastModifiedAt
							lastModifiedBy {
								associatedUser {
									id
									name
								}
							}
							name
							productId {
								plan
								type
								version
							}
							insurer {
								id
								name
								logoUrls {
									typeC
								}
							}
						}
					}
				}
			`

			const variables = {
				sort,
				skip,
				limit,
				where
			}

			const res = await api({ query, variables })
			this.isLoading = false

			if (res?.data?.errors) {
				this.isLoading = false
				return
			}

			this.listing.list = res?.data?.products_2?.list
			this.listing.numberOfItems = res?.data?.products_2?.totalCount
		},

		showDeleteDialog(plan, version, type) {
			this.$modal.show("j-dialog")
			this.selectedProduct = {
				plan: plan,
				version: version,
				type: type
			}
		},

		async onDelete(id) {
			this.isLoading = true

			const query = gql`
				mutation deleteProduct($productId: productIdInput!) {
					deleteProduct(productId: $productId) {
						status
					}
				}
			`

			const variables = {
				productId: this.selectedProduct
			}
			let res = await api({ query, variables })
			this.isLoading = false

			// Error
			if (res?.data?.deleteProduct?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.deleteProduct?.errors?.[0] || this.$t("errors.general")
					}
				})
			}

			// Delete locale
			const { plan, version, typeEnum } = this.computedList.find(
				p => p.id === id
			)

			const query2 = gql`
				mutation deleteAllL10n($key: String!) {
					deleteAllL10n(key: $key) {
						errors
						status
					}
				}
			`
			const variables2 = {
				key: `products-${plan + "|" + version + "|" + typeEnum}-name`
			}

			res = await api({ query: query2, variables: variables2 })

			if (res?.data?.deleteAllL10n?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.data?.deleteAllL10n?.errors?.[0] || this.$t("errors.general")
					}
				})
			} else {
				this.readAll()

				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("product_removed")
					}
				})
			}
		}
	},

	mounted() {
		this.readAll()
	}
}
</script>
